import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Loader2, LogOut, Moon, Sun } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';

export function TopBar() {
  const { data, isError } = useQuery({
    queryKey: ['readyz'],
    queryFn: () => api.readyz(),
    refetchInterval: 15_000,
    retry: false,
  });

  const { resolvedTheme, setTheme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loggingOut, setLoggingOut] = useState(false);

  async function logout() {
    if (loggingOut) return;
    setLoggingOut(true);
    const toastId = toast.loading('Logging out…');
    try {
      await api.auth.logout();
    } catch {
      // Even if the network call fails, still clear local state and bounce.
    }
    // Leave the dashboard FIRST, then clear the cache — clearing while the
    // dashboard is still mounted makes every query flash empty (the glitch).
    await navigate({ to: '/login' });
    queryClient.clear();
    toast.success('Logged out', { id: toastId });
  }

  let temporal: 'ok' | 'down' | 'unknown' = 'unknown';
  if (isError) temporal = 'down';
  else if (data?.temporal) temporal = data.temporal;

  return (
    <header className="flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-muted-foreground">Namespace</span>
        <Badge tone="neutral">ghost</Badge>
        <span className="ml-2 text-sm font-medium text-muted-foreground">Temporal</span>
        <Badge tone={temporal === 'ok' ? 'success' : 'failure'}>
          {temporal}
        </Badge>
      </div>
      <div className="flex items-center gap-3">
        {user && (
          <span className="hidden text-sm text-muted-foreground sm:inline" title={user.email}>
            {user.email}
          </span>
        )}
        <button
          type="button"
          onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {resolvedTheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
        <button
          type="button"
          onClick={() => void logout()}
          disabled={loggingOut}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-60"
          aria-label="Log out"
        >
          {loggingOut ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <LogOut className="h-4 w-4" />
          )}
          <span className="hidden sm:inline">{loggingOut ? 'Logging out…' : 'Log out'}</span>
        </button>
      </div>

      {/* Full-screen veil during logout so the dashboard can't flash empty
          while the (possibly slow, cross-subdomain) request resolves. */}
      {loggingOut && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm">
          <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-5 py-3 shadow-lg">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Logging out…</span>
          </div>
        </div>
      )}
    </header>
  );
}
